import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  fetchApiDelete,
  fetchApiGet,
  fetchApiPost,
  fetchApiPut,
} from "./utils";

// Enums
export enum CertificateType {
  BirthdayFund = "birthday_fund",
}

export const CertificateInstanceStatusEnum = z.enum([
  "sent",
  "invoiced",
  "paid",
]);

export type CertificateInstanceStatus = z.infer<typeof CertificateInstanceStatusEnum>;

// Validation Schemas
export const CreateTemplateValidation = z.object({
  location_name: z.string().min(1, "Location name is required"),
  template_data: z.record(z.any()),
  certificate_type: z.nativeEnum(CertificateType).optional().default(CertificateType.BirthdayFund),
});

export const UpdateTemplateValidation = z.object({
  location_name: z.string().min(1, "Location name is required"),
  template_data: z.record(z.any()),
  certificate_type: z.nativeEnum(CertificateType),
});

export const CreateInstanceValidation = z.object({
  certificate_id: z.string().min(1, "Certificate ID is required"),
  location_name: z.string().min(1, "Location name is required"),
  template_id: z.string().uuid().optional(),
  expiration: z.string().optional(),
});

export const UpdateCertificateInstanceValidation = z.object({
  status: CertificateInstanceStatusEnum.optional(),
});

export const AssociateCertificateToBreakdownValidation = z.object({
  certificate_instance_id: z.string().uuid(),
  breakdown_id: z.string().uuid(),
});

export const DisassociateCertificateFromBreakdownValidation = z.object({
  certificate_instance_id: z.string().uuid(),
  breakdown_id: z.string().uuid(),
});

// Type Definitions
export interface CertificateTemplate {
  id: string;
  location_name: string;
  template_data: Record<string, any>;
  certificate_type: CertificateType;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  created_by_user_id: string;
}

export interface CertificateInstance {
  id: string;
  certificate_id: string;
  location_name: string;
  document_id: string;
  template_id: string | null;
  status: CertificateInstanceStatus;
  expiration: string;
  created_at: string;
  deleted_at: string | null;
  created_by_user_id: string;
  created_by?: { id: string; name: string };

  birthday_fund_request_id?: string;
  birthday_fund_breakdown_id?: string;
}

// Response Types
export interface CreateTemplateResponse {
  template: CertificateTemplate;
}

export interface ListTemplatesResponse {
  templates: CertificateTemplate[];
}

export interface GetTemplateResponse {
  template: CertificateTemplate;
}

export interface UpdateTemplateResponse {
  template: CertificateTemplate;
}

export interface DeleteTemplateResponse {
  template: CertificateTemplate;
}

export interface CreateInstanceResponse {
  instance: CertificateInstance;
  presigned_put_url: string;
}

export interface ListInstancesResponse {
  instances: CertificateInstance[];
}

export interface GetInstanceResponse {
  instance: CertificateInstance;
}

export interface DeleteInstanceResponse {
  instance: CertificateInstance;
}

export interface GetCertificateAssetResponse {
  presigned_get_url: string;
}

export interface UpdateCertificateInstanceResponse {
  instance: CertificateInstance;
}

export interface AssociateCertificateToBreakdownResponse {
  instance: CertificateInstance;
}

export interface DisassociateCertificateFromBreakdownResponse {
  instance: CertificateInstance;
}

// Template Hooks
export function useListCertificateTemplates() {
  return useQuery({
    queryKey: ['fund',"birthday", "certificate", "templates"],
    queryFn: () => fetchApiGet<ListTemplatesResponse>("fund/birthday/certificate/templates"),
    retry: 1,
  });
}

export function useGetCertificateTemplate(templateId?: string | null) {
  return useQuery({
    queryKey: ['fund',"birthday", "certificate", "template", templateId],
    queryFn: () => fetchApiGet<GetTemplateResponse>(`fund/birthday/certificate/template/${templateId}`),
    retry: 1,
    enabled: !!templateId,
  });
}

export function useCreateCertificateTemplate() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof CreateTemplateValidation>) => {
    const result = await fetchApiPost<CreateTemplateResponse>(
      "fund/birthday/certificate/template",
      data
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ['fund',"birthday", "certificate", "templates"] });
    }

    return result;
  };
}

export function useUpdateCertificateTemplate() {
  const queryClient = useQueryClient();

  return async (templateId: string, data: z.infer<typeof UpdateTemplateValidation>) => {
    const result = await fetchApiPut<UpdateTemplateResponse>(
      `fund/birthday/certificate/template/${templateId}`,
      data
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ['fund',"birthday"] });
    }

    return result;
  };
}

export function useDeleteCertificateTemplate() {
  const queryClient = useQueryClient();

  return async (templateId: string) => {
    const result = await fetchApiDelete<DeleteTemplateResponse>(
      `fund/birthday/certificate/template/${templateId}`
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ['fund',"birthday"] });
    }

    return result;
  };
}

// Instance Hooks
export function useListCertificateInstances() {
  return useQuery({
    queryKey: ['fund',"birthday", "certificate", "instances"],
    queryFn: () => fetchApiGet<ListInstancesResponse>("fund/birthday/certificate/instances"),
    retry: 1,
  });
}

export function useGetCertificateInstance(instanceId?: string | null) {
  return useQuery({
    queryKey: ['fund',"birthday", "certificate", "instance", instanceId],
    queryFn: () => fetchApiGet<GetInstanceResponse>(`fund/birthday/certificate/instance/${instanceId}`),
    retry: 1,
    enabled: !!instanceId,
  });
}

export function useCreateCertificateInstance() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof CreateInstanceValidation>) => {
    const result = await fetchApiPost<CreateInstanceResponse>(
      "fund/birthday/certificate/instance",
      data
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ['fund',"birthday", "certificate", "instances"] });
    }

    return result;
  };
}

export function useDeleteCertificateInstance() {
  const queryClient = useQueryClient();

  return async (instanceId: string) => {
    const result = await fetchApiDelete<DeleteInstanceResponse>(
      `fund/birthday/certificate/instance/${instanceId}`
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ['fund',"birthday"] });
    }

    return result;
  };
}

export function useUpdateCertificateInstance() {
  const queryClient = useQueryClient();

  return async (instanceId: string, data: z.infer<typeof UpdateCertificateInstanceValidation>) => {
    const result = await fetchApiPut<UpdateCertificateInstanceResponse>(
      `fund/birthday/certificate/instance/${instanceId}`,
      data
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ['fund',"birthday"] });
    }

    return result;
  };
}

export function useAssociateCertificateToBreakdown(requestId: string) {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof AssociateCertificateToBreakdownValidation>) => {
    const result = await fetchApiPost<AssociateCertificateToBreakdownResponse>(
      `fund/birthday/requests/${requestId}/associate-certificate`,
      data
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["fund", "birthday"] });
    }

    return result;
  };
}

export function useDisassociateCertificateFromBreakdown(requestId: string) {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof DisassociateCertificateFromBreakdownValidation>) => {
    const result = await fetchApiPost<DisassociateCertificateFromBreakdownResponse>(
      `fund/birthday/requests/${requestId}/associate-certificate/remove`,
      data
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["fund", "birthday"] });
    }

    return result;
  };
}

// Certificate Asset Hook
export function useGetCertificateAsset() {
  return useQuery({
    queryKey: ["birthday", "certificate", "asset"],
    queryFn: () => fetchApiGet<GetCertificateAssetResponse>("fund/birthday/certificate/asset"),
    retry: 1,
  });
}
